/**
 * GlitchTip (protocolo Sentry) — inicialização browser-only + interceptors.
 * Vue: use attachErrorHandler de @sentry/vue em App.js (evita puxar @sentry/vue no bundle main.js).
 */
import * as Sentry from '@sentry/browser'

const IGNORE_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  /chrome-extension:/i,
  /moz-extension:/i,
]

const EXTERNAL_HOST_SUBSTRINGS = [
  'google.com',
  'gstatic.com',
  'googleapis.com',
  'clarity.ms',
  'recaptcha.net',
  'rdstation.com',
]

const SCRUB_KEYS = new Set([
  'password',
  'senha',
  'token',
  'authorization',
  'csrfmiddlewaretoken',
  'cpf',
  'cnpj',
])

function tracesSampleRateForEnvironment(environment) {
  return environment === 'dev' ? 1.0 : 0.01
}

function scrubUrlSearchParams(url) {
  try {
    const u = new URL(url, window.location.origin)
    let changed = false
    for (const key of [...u.searchParams.keys()]) {
      if (SCRUB_KEYS.has(key.toLowerCase())) {
        u.searchParams.set(key, '[Filtered]')
        changed = true
      }
    }
    return changed ? u.toString() : url
  } catch {
    return url
  }
}

function scrubValue(value, depth = 0) {
  if (depth > 10 || value === null || value === undefined) return value
  if (typeof value === 'string') return scrubUrlSearchParams(value)

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1))
  }

  if (typeof value === 'object') {
    const out = {}
    for (const [key, val] of Object.entries(value)) {
      const lk = key.toLowerCase()
      if (SCRUB_KEYS.has(lk)) {
        out[key] = '[Filtered]'
      } else {
        out[key] = scrubValue(val, depth + 1)
      }
    }
    return out
  }

  return value
}

function scrubEvent(event) {
  if (event.request?.url) {
    event.request.url = scrubUrlSearchParams(event.request.url)
  }
  if (event.request?.data) {
    event.request.data = scrubValue(event.request.data)
  }
  if (event.extra) {
    event.extra = scrubValue(event.extra)
  }
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts)
  }
}

function looksLikeExternalNetworkNoise(event, hint) {
  const message =
    hint?.originalException?.message ||
    event.exception?.values?.[0]?.value ||
    ''
  const msg = String(message).toLowerCase()
  if (!msg.includes('network') && !msg.includes('failed to fetch')) {
    return false
  }

  const reqUrl =
    event.request?.url ||
    hint?.originalException?.config?.url ||
    hint?.originalException?.request?.responseURL ||
    ''
  const urlStr = String(reqUrl).toLowerCase()
  if (!urlStr) return false

  try {
    const host = new URL(urlStr, window.location.origin).hostname
    return EXTERNAL_HOST_SUBSTRINGS.some((fragment) => host.includes(fragment))
  } catch {
    return EXTERNAL_HOST_SUBSTRINGS.some((fragment) => urlStr.includes(fragment))
  }
}

function beforeSend(event, hint) {
  try {
    if (looksLikeExternalNetworkNoise(event, hint)) return null
    scrubEvent(event)
    return event
  } catch {
    return event
  }
}

function applyDomTags() {
  if (typeof document === 'undefined' || !document.body) return

  const body = document.body
  const page = body.getAttribute('js-page')
  if (page) Sentry.setTag('page', page)

  const leilaoId = body.dataset.leilaoId
  if (leilaoId) Sentry.setTag('leilao_id', leilaoId)

  const loteId = body.dataset.loteId
  if (loteId) Sentry.setTag('lote_id', loteId)
}

function applyUserContext(cfg) {
  if (!cfg.user) return
  Sentry.setUser({
    id: String(cfg.user.id),
    username: cfg.user.username,
  })
  if (cfg.user.tipo_pessoa) {
    Sentry.setTag('tipo_pessoa', cfg.user.tipo_pessoa)
  }
}

/**
 * Inicializa o cliente browser (todas as páginas). Idempotente.
 */
export function initGlitchTipBrowser() {
  if (typeof window === 'undefined') return

  const cfg = window.GLITCHTIP_CONFIG || {}
  if (!cfg.dsn) return

  if (Sentry.getClient()) return

  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment,
    release: cfg.release || undefined,
    tracesSampleRate: tracesSampleRateForEnvironment(cfg.environment),
    beforeSend,
    ignoreErrors: IGNORE_ERRORS,
    autoSessionTracking: false,
  })

  applyUserContext(cfg)
  applyDomTags()
}

/**
 * Smoke test: ?glitchtip-debug=1 dispara um erro explícito (mesmo efeito do guia GlitchTip).
 */
export function triggerGlitchTipDebugIfRequested() {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('glitchtip-debug') !== '1') return
    setTimeout(() => {
      throw new Error('GlitchTip frontend smoke test (?glitchtip-debug=1)')
    }, 0)
  } catch {
    /* ignore */
  }
}

/**
 * Breadcrumbs + captura de 5xx no axios (window.api criado em base.html).
 */
export function setupGlitchTipAxiosInterceptors() {
  if (typeof window === 'undefined') return
  const api = window.api
  if (!api || api.__glitchTipInterceptors) return

  api.__glitchTipInterceptors = true

  api.interceptors.request.use(
    (config) => {
      if (!Sentry.getClient()) return config
      Sentry.addBreadcrumb({
        category: 'axios.request',
        type: 'http',
        data: {
          method: config.method,
          url: config.url,
        },
      })
      return config
    },
    (error) => Promise.reject(error)
  )

  api.interceptors.response.use(
    (response) => {
      if (!Sentry.getClient()) return response
      Sentry.addBreadcrumb({
        category: 'axios.response',
        type: 'http',
        data: {
          status: response.status,
          url: response.config?.url,
          method: response.config?.method,
        },
      })
      return response
    },
    (error) => {
      if (!Sentry.getClient()) return Promise.reject(error)

      const status = error.response?.status
      const cfgErr = error.config || {}
      Sentry.addBreadcrumb({
        category: 'axios.error',
        type: 'http',
        level: 'error',
        data: {
          status,
          url: cfgErr.url,
          method: cfgErr.method,
        },
      })

      if (typeof status === 'number' && status >= 500 && status < 600) {
        Sentry.captureException(error, {
          tags: { axios_http_status: String(status) },
          fingerprint: ['axios', String(cfgErr.url || ''), String(status)],
        })
      }

      return Promise.reject(error)
    }
  )
}
